import React, { useState } from "react";
import { ArrowRight, MapPin, Phone, Mail, Clock, CheckCircle2 } from "lucide-react";
import { Section, Container, Reveal, Eyebrow } from "@/components/Section";
import { useLang } from "@/contexts/LanguageContext";
import { createLead } from "@/lib/api";

export default function ContactPage() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    project_type: "", budget: "", city: "", message: "", inspiration_url: "",
  });
  const [status, setStatus] = useState({ loading: false, ok: false, err: "" });

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, ok: false, err: "" });
    try {
      await createLead({ type: "contact", locale: lang, source: "contact-page", ...form });
      setStatus({ loading: false, ok: true, err: "" });
      setForm({ first_name: "", last_name: "", email: "", phone: "", project_type: "", budget: "", city: "", message: "", inspiration_url: "" });
    } catch (err) {
      setStatus({ loading: false, ok: false, err: t.common.error });
    }
  };

  return (
    <div data-testid="contact-page">
      <Section testid="contact-hero" className="pt-32 md:pt-36">
        <Container>
          <div className="max-w-4xl">
            <Reveal><Eyebrow>{t.nav.contact}</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h1 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.06] mt-6">{t.contact.hero_h1}</h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-2xl text-base md:text-[17px] text-arven-offwhite/70 leading-relaxed">
                {t.contact.hero_sub}
              </p>
            </Reveal>
          </div>
        </Container>
      </Section>

      <Container className="pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14">
          <div className="lg:col-span-4 space-y-9" data-testid="contact-info">
            {[
              { icon: MapPin, label: t.contact.address_label, value: "Rue Jean-André Venel 4\n1400 Yverdon-les-Bains\nSuisse" },
              { icon: Phone, label: t.contact.phone_label, value: "+41 21 539 43 09", href: "tel:+41215394309" },
              { icon: Mail, label: t.contact.email_label, value: "contact@arven-cuisines.com", href: "mailto:contact@arven-cuisines.com" },
              { icon: Clock, label: t.contact.hours_label, value: t.contact.hours_value },
            ].map(({ icon: Icon, label, value, href }) => (
              <Reveal key={label}>
                <div className="flex items-start gap-4">
                  <span className="shrink-0 mt-1 w-9 h-9 rounded-full border border-arven-gold/40 flex items-center justify-center">
                    <Icon size={16} className="text-arven-gold" />
                  </span>
                  <div>
                    <p className="label-eyebrow">{label}</p>
                    {href ? (
                      <a href={href} className="mt-2 inline-block text-[15px] text-arven-offwhite/85 hover:text-arven-gold transition-colors whitespace-pre-line leading-relaxed">{value}</a>
                    ) : (
                      <p className="mt-2 text-[15px] text-arven-offwhite/85 whitespace-pre-line leading-relaxed">{value}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="lg:col-span-8">
            <Reveal>
              <form onSubmit={onSubmit} className="bg-arven-surface border border-white/10 rounded-2xl p-7 md:p-10 space-y-6" data-testid="contact-form">
                {status.ok ? (
                  <div className="py-16 text-center" data-testid="contact-success">
                    <CheckCircle2 size={42} className="text-arven-gold mx-auto" />
                    <p className="serif text-2xl md:text-3xl mt-5">{t.common.sent}</p>
                    <button
                      type="button"
                      onClick={() => setStatus({ loading: false, ok: false, err: "" })}
                      className="btn-text-gold mt-6"
                    >
                      {t.appointment.newRequest}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="field-label">{t.contact.form.firstName}</label>
                        <input name="first_name" value={form.first_name} onChange={onChange} required data-testid="input-first-name" />
                      </div>
                      <div>
                        <label className="field-label">{t.contact.form.lastName}</label>
                        <input name="last_name" value={form.last_name} onChange={onChange} required data-testid="input-last-name" />
                      </div>
                      <div>
                        <label className="field-label">{t.contact.form.email}</label>
                        <input type="email" name="email" value={form.email} onChange={onChange} required data-testid="input-email" />
                      </div>
                      <div>
                        <label className="field-label">{t.contact.form.phone}</label>
                        <input name="phone" value={form.phone} onChange={onChange} data-testid="input-phone" />
                      </div>
                      <div>
                        <label className="field-label">{t.contact.form.type}</label>
                        <select name="project_type" value={form.project_type} onChange={onChange} required data-testid="input-project-type">
                          <option value="">—</option>
                          {t.contact.form.types.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="field-label">{t.contact.form.budget}</label>
                        <select name="budget" value={form.budget} onChange={onChange} data-testid="input-budget">
                          <option value="">—</option>
                          {t.contact.form.budgets.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="field-label">{t.contact.form.city}</label>
                        <input name="city" value={form.city} onChange={onChange} data-testid="input-city" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="field-label">{t.contact.form.message}</label>
                        <textarea name="message" rows={5} value={form.message} onChange={onChange} required data-testid="input-message" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="field-label">{t.contact.form.inspiration}</label>
                        <input name="inspiration_url" value={form.inspiration_url} onChange={onChange} placeholder="https://..." data-testid="input-inspiration" />
                      </div>
                    </div>
                    {status.err && <p className="text-[13px] text-[#E07A6A]">{status.err}</p>}
                    <div className="pt-2">
                      <button type="submit" disabled={status.loading} className="btn-gold" data-testid="contact-submit">
                        {status.loading ? t.common.sending : t.contact.form.submit} <ArrowRight size={14} />
                      </button>
                    </div>
                  </>
                )}
              </form>
            </Reveal>
          </div>
        </div>
      </Container>
    </div>
  );
}
